import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { injectIntl } from "react-intl";
import { createSelector } from 'reselect';

import ClientConfigurationPageComponent from './ClientConfigurationPage';
import { createConfiguration, createAccountConfiguration, deleteConfiguration, get, getConfiguration, getAccountConfiguration, resetClientConfiguration, resetClientAccountConfiguration, updateConfiguration, updateAccountConfiguration, getFHIRConfiguration, updateFHIRConfiguration, createFHIRConfiguration, resetClientFHIRConfiguration, testFHIRConnection } from '../../../../store/actions/clients';
import { createClientSelector, selectClientConfiguration, selectClientAccountConfiguration, selectClientFHIRConfiguration } from '../../../../store/selectors/clients';
import { RootState } from "../../../../store/reducers";
import { selectIsSuperAdmin } from '../../../../store/selectors/session';

const selectClientId = (state, props) => props.match.params.clientId;


const selectInitialValues = createSelector(
    selectClientConfiguration,
    (clientConfigs) => {
        if (clientConfigs && clientConfigs.configuration) {
            const payload = {
                prefixExcluded: clientConfigs?.configuration?.prefixExcluded,
                postfixExcluded: clientConfigs?.configuration?.postfixExcluded,
                fileExtensionsExcluded: clientConfigs?.configuration?.fileExtensionsExcluded,
                notifyOnFileNotReceived: clientConfigs?.configuration?.notifyOnFileNotReceived,
                notifyAfterDaysFileNotReceived: clientConfigs?.configuration?.notifyAfterDaysFileNotReceived,
                customClaimStatusEnabled: clientConfigs?.configuration?.customClaimStatusEnabled,
                summaryDashboardEnabled: clientConfigs?.configuration?.summaryDashboardEnabled ?? true,
                usePlanIdMapping: clientConfigs?.configuration?.usePlanIdMapping ?? false,
                eapgEnabled: clientConfigs?.configuration?.eapgEnabled ?? false,
                aiEnabled: clientConfigs?.configuration?.aiEnabled ?? false,
                claimAnalysisChatbotEnabled: clientConfigs?.configuration?.claimAnalysisChatbotEnabled ?? false,
                contractDocumentChatbotEnabled: clientConfigs?.configuration?.contractDocumentChatbotEnabled ?? false,
                supplementaryKnowledgeableEnabled: clientConfigs?.configuration?.supplementaryKnowledgeBaseEnabled ?? false,
                supplementaryPromptEnabled: clientConfigs?.configuration?.supplementaryPromptEnabled ?? false,
                claimAnalysisFollowupEnabled: clientConfigs?.configuration?.claimAnalysisFollowupEnabled ?? false,
                contractFollowupEnabled: clientConfigs?.configuration?.contractFollowupEnabled ?? false,
                customRCMTeamAssignmentEnabled: clientConfigs?.configuration?.customRCMTeamAssignmentEnabled ?? false,
                claimSemanticSearchEnabled: clientConfigs?.configuration?.claimSemanticSearchEnabled ?? false,
                siteName: clientConfigs?.configuration?.siteName || "",
                remitExtAssignerDedupEnabled: clientConfigs?.configuration?.remitExtAssignerDedupEnabled ?? false,

            }
            return payload;
        }
        return {
            prefixExcluded: '',
            postfixExcluded: '',
            fileExtensionsExcluded: '',
            notifyOnFileNotReceived: false,
            notifyAfterDaysFileNotReceived: 0,
            customClaimStatusEnabled: false,
            summaryDashboardEnabled: true,
            eapgEnabled: false,
            aiEnabled: false,
            claimAnalysisChatbotEnabled: false,
            contractDocumentChatbotEnabled: false,
            supplementaryKnowledgeableEnabled: false,
            supplementaryPromptEnabled: false,
            claimAnalysisFollowupEnabled: false,
            contractFollowupEnabled: false,
            usePlanIdMapping: false,
            customRCMTeamAssignmentEnabled: false,
            claimSemanticSearchEnabled: false,
            remitExtAssignerDedupEnabled: false,
            siteName: ""
        }
    }
);

const selectInitialValuesAccount = createSelector(
    selectClientAccountConfiguration,
    (clientAccountConfigs) => {
        if (clientAccountConfigs) {
            const payload = {
                mergeClaims: clientAccountConfigs?.mergeClaims,
                accountIdentifierNumbers: clientAccountConfigs?.accountIdentifierNumbers ?? '',
                updatedAt: clientAccountConfigs?.updatedAt,
                updatedBy: clientAccountConfigs?.updatedBy,
            }
            return payload;
        }
        return {
            mergeClaims: false,
            accountIdentifierNumbers: ''
        }
    }
);

const selectIsEditMode = createSelector(
    selectClientConfiguration,
    (clientConfigs) => {
        if (clientConfigs && clientConfigs?.configuration) {
            return true
        }
        return false
    }
);

const selectIsAccountEditMode = createSelector(
    selectClientAccountConfiguration,
    (clientAccountConfigs) => {
        if (clientAccountConfigs && clientAccountConfigs?.updatedAt) {
            return true
        }
        return false
    }
);

const selectInitialValuesFHIR = createSelector(
    selectClientFHIRConfiguration,
    (clientFHIRConfigs) => {
        if (clientFHIRConfigs) {
            const payload = {
                providerType: clientFHIRConfigs?.ProviderType,
                practiceId: clientFHIRConfigs?.PracticeId,
                providerClientSecret: clientFHIRConfigs?.ProviderClientSecret,
                providerClientId: clientFHIRConfigs?.ProviderClientId,
                isPatientDetailsEnabled: clientFHIRConfigs?.IsPatientDetailsEnabled,
                isClaimNotedEnabled: clientFHIRConfigs?.IsClaimNotedEnabled,
            }
            return payload;
        }
        return {
            providerType: 0,
            practiceId: '',
            providerClientSecret: '',
            providerClientId: '',
            isPatientDetailsEnabled: false,
            isClaimNotedEnabled: false
        }
    }
);

const selectIsFHIREditMode = createSelector(
    selectClientFHIRConfiguration,
    (clientFHIRConfigs) => {
        if (clientFHIRConfigs) {
            return true
        }
        return false
    }
);

const selectClient = createClientSelector(selectClientId);

export const ClientConfigurationPage = injectIntl(
    connect(
        (state: RootState, props) => ({
            initialValues: selectInitialValues(state as never),
            initialValuesAccount: selectInitialValuesAccount(state as never),
            initialValuesFHIR: selectInitialValuesFHIR(state as never),
            clientId: selectClientId(state, props),
            isEdit: selectIsEditMode(state as never),
            isAccountEdit: selectIsAccountEditMode(state as never),
            isFHIREdit: selectIsFHIREditMode(state as never),
            configuration: selectClientConfiguration(state as never),
            client: selectClient(state, props),

            getClientPending: state.clients.getConfiguration.pending,
            getClientAccountPending: state.clients.getAccountConfiguration.pending,
            getClientFHIRPending: state.clients.getFHIRConfiguration.pending,

            createClientPending: state.clients.createConfiguration.pending,
            createClientAccountPending: state.clients.createAccountConfiguration.pending,
            createClientFHIRPending: state.clients.createFHIRConfiguration.pending,
            testFHIRConnectionPending:state.clients.testFHIRConnection.pending,

            updateClientPending: state.clients.updateConfiguration.pending,
            updateAccountPending: state.clients.updateAccountConfiguration.pending,
            updateFHIRPending: state.clients.updateFHIRConfiguration.pending,

            deleteClientPending: state.clients.deleteConfiguration.pending,
            isSuperAdmin: selectIsSuperAdmin(state),
        }),
        (dispatch) => ({
            actions: bindActionCreators({
                getConfiguration: getConfiguration,
                getAccountConfiguration: getAccountConfiguration,
                getFHIRConfiguration: getFHIRConfiguration,
                createConfiguration: createConfiguration,
                updateConfiguration: updateConfiguration,
                updateAccountConfiguration,
                createAccountConfiguration,
                updateFHIRConfiguration,
                createFHIRConfiguration,
                testFHIRConnection,
                deleteConfiguration: deleteConfiguration,
                resetClientConfiguration: resetClientConfiguration,
                resetClientAccountConfiguration: resetClientAccountConfiguration,
                resetClientFHIRConfiguration: resetClientFHIRConfiguration,
                getClient: get,
            }, dispatch)
        })
    )(ClientConfigurationPageComponent)
);